import { isSynced as resolverIsSynced } from 'services/resolver/synced'
import { List, Map } from 'immutable'
import { all as BluebirdAll } from 'bluebird'
import { SET_RESOLVER_DATA, SET_RESOLVER_LOCATION } from 'services/resolver/actions'
import {
  getAudioLangs,
  get as getDetail,
  getPlaceholder,
  getSubtitleLangs,
} from 'services/detail'
import { SET_APP_BOOTSTRAP_PHASE } from 'services/app/actions'
import {
  SET_DETAIL_DATA_PLACEHOLDER,
  SET_DETAIL_ID_PATH_TYPE,
  SET_DETAIL_DATA,
  GET_DETAIL_RELATED_CONTENT,
  SET_DETAIL_RELATED_CONTENT_PROCESSING,
  SET_DETAIL_RELATED_CONTENT_DATA,
  setSeriesDetailData,
  setFeaturedEpisodeData,
  setDetailMediaInfoData,
  setDetailRelatedVideo,
  setDetailRelatedContentData,
  setDetailProcessing,
  GET_SEASON_EPISODES,
} from 'services/detail/actions'
import {
  SET_USER_DATA_LANGUAGE,
} from 'services/user/actions'
import { TYPE_CONTENT_PRODUCT } from 'services/content-type'
import { set as setInboundTracking } from 'services/inbound-tracking'
import { SET_INBOUND_TRACKING_DATA } from 'services/inbound-tracking/actions'
import { get as getConfig } from 'config'
import _get from 'lodash/get'
import _map from 'lodash/map'
import _size from 'lodash/size'
import _filter from 'lodash/filter'
import _merge from 'lodash/merge'
import { isSeries, isVideo } from 'services/url'
import { getRelatedContent, get as getRelated } from 'services/related'
import { getProducts, resetNodesByKey } from 'services/node/actions'
import { getPrimary } from 'services/languages'
import { DEFAULT_LIMIT, getFeaturedEpisode } from 'services/tiles'
import { get as getMediaData } from 'services/media'
import { PRODUCTS_KEY } from 'services/node/entities'
import {
  STORE_KEY_DETAIL_SERIES_SEASON,
  STORE_KEY_DETAIL_SERIES_FIRST_EPISODE,
} from 'services/store-keys'
import { getTilesData } from 'services/tiles/actions'
import { selectLocation } from 'services/resolver/selectors'
import { setCommentsVisible } from 'services/comments/actions'
import { selectIsCommentsVisible } from 'services/comments/selectors'
import {
  selectCurrentSeasonNumber,
  selectIsEpisode,
  selectIsSeries,
  selectIsVideo,
  selectSeriesId,
} from './selectors'

const config = getConfig()
const detailPageV2 = _get(config, ['features', 'detailPageV2'])

function shouldUpdateData ({ state }) {
  const { resolver } = state
  const path = resolver.getIn(['path'])

  if ((isSeries(path) || isVideo(path)) && detailPageV2) {
    return true
  }
  return false
}

function detailIsSynced (detail, resolver) {
  const resolvedId = resolver.getIn(['data', 'id'])
  return (
    resolvedId === detail.get('id')
    && resolvedId === detail.getIn(['data', 'id'])
  )
}

async function getDetailData (dispatch, options) {
  const {
    id, path, type, auth, language,
  } = options
  try {
    dispatch({
      type: SET_DETAIL_ID_PATH_TYPE,
      payload: {
        id, path, type, processing: true,
      },
    })

    const data = await getDetail({ id, auth, language })
    dispatch({
      type: SET_DETAIL_DATA,
      payload: { data, processing: false },
    })
  } catch (e) {
    // Do nothing for now
  }
}

function setDetailDataPlaceholder (dispatch) {
  dispatch({
    type: SET_DETAIL_DATA_PLACEHOLDER,
    payload: { data: getPlaceholder(), processing: false },
  })
}

// -----------------------------------
// Watcher for checking data "server side"
// -----------------------------------
export function watchSetAppBootstrapPhase ({ before }) {
  return before(SET_APP_BOOTSTRAP_PHASE, async ({ dispatch, state, action }) => {
    const {
      app, resolver, user, detail, auth,
    } = state

    const { payload } = action
    const resolvedId = resolver.getIn(['data', 'id'])
    const currentBootstrapComplete = app.get('bootstrapComplete')
    const nextBootstrapComplete = _get(payload, 'isComplete')
    const resolverProcessing = resolver.get('processing')
    const detailPlaceholderExists = detail.get('placeholderExists')
    const resolverDetailPathSynced = resolver.getIn(['path']) === detail.get('path')

    // set placeholder, if needed
    if (
      resolverProcessing
      || (!detailPlaceholderExists && !resolverDetailPathSynced)
    ) {
      setDetailDataPlaceholder(dispatch)
    }

    // get data, only once
    if (nextBootstrapComplete && nextBootstrapComplete !== currentBootstrapComplete) {
      getDetailData(dispatch, {
        id: resolvedId,
        path: resolver.getIn(['path']),
        type: resolver.getIn(['data', 'type']),
        language: user.getIn(['data', 'language'], List()),
        auth,
        include2kImages: true,
        include4kImages: false,
      })
    }
  })
    .when(shouldUpdateData)
}

// -----------------------------------
// Watcher for componentDidMount/DidUpdate on Detail pages
// -----------------------------------
export function watchSetResolverData ({ after }) {
  return after(SET_RESOLVER_DATA, async ({ dispatch, state }) => {
    const {
      resolver, detail, user, auth,
    } = state
    const resolvedId = resolver.getIn(['data', 'id'])
    const resolverSynced = resolverIsSynced(resolver, global.location)
    const detailSynced = detailIsSynced(detail, resolver)
    const resolverProcessing = resolver.get('processing')
    const detailPlaceholderExists = detail.get('placeholderExists')
    const resolverDetailPathSynced = resolver.getIn(['path']) === detail.get('path')

    // We are only interested when/if entity data has been updated

    // set placeholder, if needed
    if (
      resolverProcessing
      || (!detailPlaceholderExists && !resolverDetailPathSynced)
    ) {
      setDetailDataPlaceholder(dispatch)
    }

    // get data
    if (
      resolvedId > 0
      && resolverSynced
      && !detailSynced
      && !detail.get('processing')
    ) {
      getDetailData(dispatch, {
        id: resolvedId,
        path: resolver.getIn(['path']),
        type: resolver.getIn(['data', 'type']),
        language: user.getIn(['data', 'language'], List()),
        auth,
        include2kImages: true,
        include4kImages: false,
      })
    }
  })
    .when(shouldUpdateData)
}

const getVideoNodeAdditionalDetailData = async (dispatch, state) => {
  const { auth, detail, user } = state

  const language = user.getIn(['data', 'language'], List())
  const id = detail.getIn(['data', 'id'])
  const featureId = detail.getIn(['data', 'feature', 'id'])
  const contentType = detail.getIn(['data', 'contentType'])

  const mediaDataRequest = getMediaData({
    id: featureId,
    auth,
    user,
  })

  const relatedVideoRequest = getRelated({
    id,
    language,
    country: auth.get('country'),
    auth,
  })

  const relatedContentRequest = getRelatedContent({
    auth,
    contentId: id,
    contentType,
  })

  const [
    mediaData,
    relatedVideo,
    relatedContent,
  ] = await BluebirdAll([
    mediaDataRequest,
    relatedVideoRequest,
    relatedContentRequest,
  ])

  const audioLangs = getAudioLangs(_get(mediaData, 'byLang'))
  const subtitleLangs = getSubtitleLangs(_get(mediaData, 'textTracks'))

  dispatch(setDetailMediaInfoData({ audioLangs, subtitleLangs }))
  dispatch(setDetailRelatedVideo(_get(relatedVideo, 'titles')))
  dispatch(setDetailRelatedContentData(relatedContent))
}

const getEpisodeNodeAdditionalDetailData = async (dispatch, state) => {
  const { auth, detail, user } = state

  const language = user.getIn(['data', 'language'], List())
  const id = detail.getIn(['data', 'id'])
  const featureId = detail.getIn(['data', 'feature', 'id'])
  const seriesId = detail.getIn(['data', 'seriesId'])
  const seriesPath = detail.getIn(['data', 'seriesPath'])
  const contentType = detail.getIn(['data', 'contentType'])

  const seriesDetailDataRequest = getDetail({
    id: seriesId,
    path: seriesPath,
    auth,
    language,
  })

  const mediaDataRequest = getMediaData({
    id: featureId,
    auth,
    user,
  })

  const relatedVideoRequest = getRelated({
    id,
    language,
    country: auth.get('country'),
    auth,
  })

  const relatedEpisodeContentRequest = getRelatedContent({
    auth,
    contentId: id,
    contentType,
  })

  const relatedSeriesContentRequest = getRelatedContent({
    auth,
    contentId: seriesId,
    contentType: 'series',
  })

  const [
    seriesDetailData,
    mediaData,
    relatedVideo,
    relatedEpisodeContent,
    relatedSeriesContent,
  ] = await BluebirdAll([
    seriesDetailDataRequest,
    mediaDataRequest,
    relatedVideoRequest,
    relatedEpisodeContentRequest,
    relatedSeriesContentRequest,
  ])

  const audioLangs = getAudioLangs(_get(mediaData, 'byLang'))
  const subtitleLangs = getSubtitleLangs(_get(mediaData, 'textTracks'))
  const relatedContent = _merge(relatedEpisodeContent, relatedSeriesContent)

  dispatch(setSeriesDetailData(seriesDetailData))
  dispatch(setDetailMediaInfoData({ audioLangs, subtitleLangs }))
  dispatch(setDetailRelatedVideo(_get(relatedVideo, 'titles')))
  dispatch(setDetailRelatedContentData(relatedContent))
}

const getSeriesNodeAdditionalDetailData = async (dispatch, state) => {
  const { auth, detail, user } = state

  const language = user.getIn(['data', 'language'], List())
  const id = detail.getIn(['data', 'id'])
  const contentType = detail.getIn(['data', 'contentType'])

  const featuredEpisodeData = await getFeaturedEpisode({
    id,
    auth,
    language,
  })

  const featureId = _get(featuredEpisodeData, 'titles[0].feature.id')
  const relatedId = _get(featuredEpisodeData, 'titles[0].id')

  dispatch(setFeaturedEpisodeData(_get(featuredEpisodeData, 'titles')))

  const mediaDataRequest = getMediaData({
    id: featureId,
    auth,
    user,
  })

  const relatedVideoRequest = getRelated({
    id: relatedId,
    language,
    country: auth.get('country'),
    auth,
  })

  const relatedSeriesContentRequest = getRelatedContent({
    auth,
    contentId: id,
    contentType,
  })

  const relatedEpisodeContentRequest = getRelatedContent({
    auth,
    contentId: relatedId,
    contentType,
  })

  const [
    mediaData,
    relatedVideo,
    relatedSeriesContent,
    relatedEpisodeContent,
  ] = await BluebirdAll([
    mediaDataRequest,
    relatedVideoRequest,
    relatedSeriesContentRequest,
    relatedEpisodeContentRequest,
  ])

  const audioLangs = getAudioLangs(_get(mediaData, 'byLang'))
  const subtitleLangs = getSubtitleLangs(_get(mediaData, 'textTracks'))
  const relatedContent = _merge(relatedSeriesContent, relatedEpisodeContent)

  dispatch(setDetailMediaInfoData({ audioLangs, subtitleLangs }))
  dispatch(setDetailRelatedVideo(_get(relatedVideo, 'titles')))
  dispatch(setDetailRelatedContentData(relatedContent))
}

export function watchSetAdditionalDetailData ({ after }) {
  return after(SET_DETAIL_DATA, async ({ dispatch, state }) => {
    const isEpisodeNode = selectIsEpisode(state)
    const isSeriesNode = selectIsSeries(state)
    const isVideoNode = selectIsVideo(state)

    try {
      dispatch(setDetailProcessing(true))
      // to reset products
      dispatch(resetNodesByKey(PRODUCTS_KEY))

      if (isEpisodeNode) {
        await getEpisodeNodeAdditionalDetailData(dispatch, state)
      }

      if (isSeriesNode) {
        await getSeriesNodeAdditionalDetailData(dispatch, state)
      }

      if (isVideoNode) {
        await getVideoNodeAdditionalDetailData(dispatch, state)
      }

      dispatch(setDetailProcessing(false))
    } catch (error) {
      // do nothing
    }
  })
    .when(shouldUpdateData)
}

export function watchSetSeasonEpisodesData ({ after }) {
  return after(
    [GET_SEASON_EPISODES, SET_DETAIL_RELATED_CONTENT_DATA],
    async ({ dispatch, state, action }) => {
      const { auth, user } = state
      const {
        season: payloadSeason,
        updateData = false,
        page = 0,
      } = action.payload

      const isVideoNode = selectIsVideo(state)

      if (isVideoNode) {
        return
      }

      const language = user.getIn(['data', 'language'], List())
      const location = selectLocation(state)
      const seriesId = selectSeriesId(state)
      const season = payloadSeason
        || _get(location, 'query.season')
        || selectCurrentSeasonNumber(state)

      dispatch(getTilesData(
        STORE_KEY_DETAIL_SERIES_SEASON,
        seriesId,
        Map({ type: 'node', language, season }),
        page,
        DEFAULT_LIMIT,
        updateData,
        location,
        auth.get('uid'),
        auth.get('jwt'),
      ))

      // Get the S1E1 data
      if (season !== 1) {
        dispatch(getTilesData(
          STORE_KEY_DETAIL_SERIES_FIRST_EPISODE,
          seriesId,
          Map({ type: 'node', language, season: 1 }),
          page,
          1,
          updateData,
          location,
          auth.get('uid'),
          auth.get('jwt'),
        ))
      }
    },
  )
    .when(shouldUpdateData)
}

// -----------------------------------
// Watcher for SET_DETAIL_DATA
// -----------------------------------
export function watchSetDetailData ({ after }) {
  return after(SET_DETAIL_DATA, async ({ dispatch, state }) => {
    const { auth, detail, inboundTracking } = state
    const detailId = detail.getIn(['data', 'id'])
    // set tracking
    const recordedId = inboundTracking.getIn(['data', 'ci_id'])
    const contentType = detail.getIn(['data', 'impressionType'])
    const uid = auth.get('uid')

    if (recordedId !== detailId) {
      try {
        const data = await setInboundTracking({
          uid,
          data: {
            ci_type: contentType,
            ci_id: detailId,
          },
          auth,
        })

        dispatch({
          type: SET_INBOUND_TRACKING_DATA,
          payload: { data, processing: false },
        })
      } catch (e) {
        // do nothing for now
      }
    }
  })
    .when(shouldUpdateData)
}

// -----------------------------------
// Watcher for GET_DETAIL_RELATED_CONTENT
// -----------------------------------
export function watchGetDetailRelatedContent ({ after }) {
  return after(GET_DETAIL_RELATED_CONTENT, async ({ dispatch, state, action }) => {
    const { auth } = state
    const { payload } = action
    const { contentId, contentType, contentReferenceType } = payload

    dispatch({
      type: SET_DETAIL_RELATED_CONTENT_PROCESSING,
      payload: true,
    })

    const related = await getRelatedContent(
      {
        auth,
        contentId,
        contentType,
        contentReferenceType,
      },
    )

    dispatch({
      type: SET_DETAIL_RELATED_CONTENT_DATA,
      payload: { data: related, processing: false },
    })
  })
}

// -----------------------------------
// Watcher for SET_DETAIL_RELATED_CONTENT_DATA
// -----------------------------------
export function watchSetDetailRelatedContentData ({ after }) {
  return after(SET_DETAIL_RELATED_CONTENT_DATA, async ({ dispatch, state, action }) => {
    const { user } = state
    const { payload } = action
    const { data } = payload
    const relatedProducts = _filter(_get(data, 'data', []), (c) => _get(c, 'contentType') === TYPE_CONTENT_PRODUCT)
    const language = getPrimary(user.getIn(['data', 'language']))

    if (_size(relatedProducts)) {
      const ids = _map(relatedProducts, (p) => {
        return _get(p, 'contentId')
      })

      // fetch the product nodes
      dispatch(getProducts({ ids, language }))
    }
  })
}

// -----------------------------------
// Watcher for SET_DETAIL_RELATED_CONTENT_DATA
// -----------------------------------
export function watchSetUserDataLanguage ({ after }) {
  return after(
    SET_USER_DATA_LANGUAGE,
    async ({
      dispatch,
      state,
      prevState,
      action,
    }) => {
      const {
        auth,
        detail,
        user: nextUser,
      } = state
      const { user: prevUser } = prevState
      const { payload } = action
      const { data } = payload
      const relatedProducts = _filter(_get(data, 'data', []), (c) => _get(c, 'contentType') === TYPE_CONTENT_PRODUCT)
      const prevLanguage = getPrimary(prevUser.getIn(['data', 'language']))
      const nextLanguage = getPrimary(nextUser.getIn(['data', 'language']))

      // has the language changed
      if (prevLanguage !== nextLanguage) {
        // re-fetch the related products
        if (_size(relatedProducts)) {
          const ids = _map(relatedProducts, (p) => {
            return _get(p, 'contentId')
          })

          // fetch the product nodes
          dispatch(getProducts({ ids, language: nextLanguage }))
        }

        // re-fetch detail page data
        getDetailData(dispatch, {
          id: detail.get('id'),
          path: detail.get('path'),
          type: detail.get('type'),
          language: nextUser.getIn(['data', 'language'], List()),
          auth,
          include2kImages: true,
          include4kImages: false,
        })
      }
    },
  )
}

export function pageUnmount ({ after }) {
  return after(SET_RESOLVER_LOCATION, async ({ dispatch }) => {
    dispatch(setCommentsVisible(false))
  }).when(({ state }) => {
    return selectIsCommentsVisible(state)
  })
}
